import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarDays, Filter, History, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { heroText } from "@/lib/pageHero";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import { EventCard } from "@/components/site/EventCard";
import { EventCalendar } from "@/components/site/EventCalendar";
import { PageHero } from "@/components/site/PageHero";
import { PageContentBlocks } from "@/components/site/PageContentBlocks";
import { useLang } from "@/context/LanguageContext";
import { EditableText } from "@/components/site/edit/EditableText";
import { SectionShell } from "@/components/site/edit/SectionShell";

export default function EventsPage() {
  const { t, tl, lang } = useLang();
  const [active, setActive] = useState("All Events");
  const [showLater, setShowLater] = useState(false);
  const [showPast, setShowPast] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const { data: events = [] } = useQuery({
    queryKey: ["events", "upcoming"],
    queryFn: async () => (await api.get("/events?upcoming=true")).data,
  });
  const { data: pastEvents = [] } = useQuery({
    queryKey: ["events", "past", "all"],
    queryFn: async () => (await api.get("/events?past=true")).data,
    enabled: showPast,
  });
  const { data: categories = [] } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => (await api.get("/categories")).data,
  });
  const { data: settings = {} } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const headerImg = settings?.page_headers?.events;
  const ht = heroText(settings, "events", lang, t, "eventspage");

  // Only show filters for categories that actually have events (hides services & empty categories)
  const eventCats = Array.from(new Set(events.map((e) => e.category).filter(Boolean)));
  const labelFor = (catValue) => {
    const match = categories.find((c) => c.title === catValue);
    return match ? tl(match, "title") : catValue;
  };
  const filters = [{ value: "All Events", label: t("eventspage.all") }, ...eventCats.map((c) => ({ value: c, label: labelFor(c) }))];
  const filtered = active === "All Events" ? events : events.filter((e) => e.category === active);

  // "All Events" shows only the next 2 months by default, with a button to reveal later events.
  const isAll = active === "All Events";
  const cutoff = new Date(); cutoff.setMonth(cutoff.getMonth() + 2);
  const within = isAll ? filtered.filter((e) => new Date(`${e.date}T00:00:00`) <= cutoff) : filtered;
  const laterCount = isAll ? filtered.length - within.length : 0;
  const visible = isAll && !showLater ? within : filtered;
  const changeFilter = (v) => { setActive(v); setShowLater(false); };
  const laterLabel = { en: `Show ${laterCount} more event${laterCount === 1 ? "" : "s"}`, cs: `Zobrazit dalších ${laterCount}`, he: `הצג ${laterCount} אירועים נוספים`, ru: `Показать ещё ${laterCount}` }[lang] || `Show ${laterCount} more events`;

  // Month headers (only in the "All Events" view): group chronological events under localized month/year labels.
  const localeMap = { en: "en-US", cs: "cs-CZ", he: "he-IL", ru: "ru-RU" };
  const monthLabel = (dateStr) => {
    const d = new Date(`${dateStr}T00:00:00`);
    const label = new Intl.DateTimeFormat(localeMap[lang] || "en-US", { month: "long", year: "numeric" }).format(d);
    return label.charAt(0).toUpperCase() + label.slice(1);
  };
  const monthGroups = (() => {
    const groups = [];
    let currentKey = null;
    visible.forEach((ev) => {
      const key = ev.date ? ev.date.slice(0, 7) : "no-date";
      if (key !== currentKey) {
        groups.push({ key, label: ev.date ? monthLabel(ev.date) : "", events: [] });
        currentKey = key;
      }
      groups[groups.length - 1].events.push(ev);
    });
    return groups;
  })();
  const pastFiltered = isAll ? pastEvents : pastEvents.filter((e) => e.category === active);
  const pastGroups = (() => {
    const groups = [];
    let currentKey = null;
    pastFiltered.forEach((ev) => {
      const key = ev.date ? ev.date.slice(0, 7) : "no-date";
      if (key !== currentKey) {
        groups.push({ key, label: ev.date ? monthLabel(ev.date) : "", events: [] });
        currentKey = key;
      }
      groups[groups.length - 1].events.push(ev);
    });
    return groups;
  })();

  return (
    <div className="relative bg-[#F9F8F6] min-h-screen overflow-x-hidden" data-testid="events-page">
      <div className="grain" />
      <Navbar />

      <PageHero image={headerImg} eyebrow={ht("eyebrow")} title={ht("title")} description={ht("desc")} icon={CalendarDays} testid="events-hero" />

      <PageContentBlocks page="events" position="top" />

      <SectionShell id="events-sec-1" label="Section 1" noReorder>
      <section className="pt-12 sm:pt-14 pb-10">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal delay={0.1}>
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="inline-flex items-center gap-2 text-sm font-medium text-[#475569] mr-1"><Filter size={16} /> <EditableText k="eventspage.filter" as="span" /></span>
              {filters.map((f) => (
                <button
                  key={f.value}
                  data-testid={`filter-${f.value.replace(/[^a-zA-Z]/g, "-").toLowerCase()}`}
                  onClick={() => changeFilter(f.value)}
                  className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors border ${
                    active === f.value
                      ? "bg-[#BE923C] text-white border-[#BE923C]"
                      : "bg-white text-[#0F172A] border-[#0F172A]/10 hover:border-[#BE923C]"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </Reveal>
        </div>
      </section>
      </SectionShell>

      <SectionShell id="events-sec-2" label="Section 2" noReorder>
      <section className="pb-24 sm:pb-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <AnimatePresence mode="popLayout">
            {visible.length === 0 ? (
              <motion.div
                key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                data-testid="events-page-empty"
                className="rounded-3xl border border-dashed border-[#0F172A]/15 py-24 text-center"
              >
                <CalendarDays size={40} className="mx-auto text-[#0F172A]/20" />
                <p className="mt-4 font-serif text-2xl text-[#0F172A]"><EditableText k="eventspage.none_title" as="span" /></p>
                <p className="mt-2 text-[#475569]"><EditableText k="eventspage.none_desc" as="span" /></p>
              </motion.div>
            ) : isAll ? (
              <motion.div layout className="space-y-12">
                {monthGroups.map((group) => (
                  <div key={group.key} data-testid={`month-group-${group.key}`}>
                    {group.label && (
                      <div className="mb-6 flex items-center gap-3">
                        <span
                          data-testid={`month-header-${group.key}`}
                          className="inline-flex items-center rounded-full bg-[#0F172A] text-[#E8C77B] px-5 py-2 text-sm font-semibold tracking-wide shadow-sm"
                        >
                          {group.label}
                        </span>
                        <span className="h-px flex-1 bg-[#0F172A]/10" />
                      </div>
                    )}
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                      {group.events.map((ev, i) => (
                        <motion.div key={ev.id} layout initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}>
                          <EventCard event={ev} index={i} />
                        </motion.div>
                      ))}
                    </div>
                  </div>
                ))}
              </motion.div>
            ) : (
              <motion.div layout className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {visible.map((ev, i) => (
                  <motion.div key={ev.id} layout initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}>
                    <EventCard event={ev} index={i} />
                  </motion.div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          {isAll && !showLater && laterCount > 0 && (
            <div className="mt-10 text-center">
              <button
                data-testid="show-later-events-btn"
                onClick={() => setShowLater(true)}
                className="inline-flex items-center gap-2 rounded-full bg-white border border-[#0F172A]/12 hover:border-[#BE923C] text-[#0F172A] font-semibold px-7 py-3 transition-colors"
              >
                <CalendarDays size={17} className="text-[#BE923C]" /> {laterLabel}
              </button>
            </div>
          )}

          <div className="mt-14 pt-10 border-t border-[#0F172A]/10">
            <button
              type="button"
              data-testid="events-past-toggle"
              onClick={() => setShowPast((v) => !v)}
              className="inline-flex items-center gap-2 rounded-full border border-[#0F172A]/15 bg-white px-6 py-3 text-sm font-semibold text-[#0F172A] transition-colors hover:border-[#BE923C] hover:text-[#BE923C]"
            >
              <History size={16} />
              {showPast ? t("program.past_events_hide") : t("program.past_events")}
              <ChevronDown size={16} className={`transition-transform ${showPast ? "rotate-180" : ""}`} />
            </button>

            {showPast && (
              <div className="mt-8 space-y-12" data-testid="events-past-list">
                {pastFiltered.length === 0 ? (
                  <p data-testid="events-past-empty" className="text-[#475569]">{t("program.past_events_empty")}</p>
                ) : pastGroups.map((group) => (
                  <div key={group.key} data-testid={`past-month-group-${group.key}`}>
                    {group.label && (
                      <div className="mb-6 flex items-center gap-3">
                        <span className="inline-flex items-center rounded-full bg-[#E8E6E1] text-[#475569] px-5 py-2 text-sm font-semibold tracking-wide">{group.label}</span>
                        <span className="h-px flex-1 bg-[#0F172A]/10" />
                      </div>
                    )}
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                      {group.events.map((ev, i) => (
                        <Reveal key={ev.id} delay={(i % 3) * 0.05}><EventCard event={ev} index={i} /></Reveal>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
      </SectionShell>

      <EventCalendar />

      <PageContentBlocks page="events" position="bottom" />

      <Footer />
    </div>
  );
}
